import React, {useEffect, useState} from 'react'
import {Box, Stack, Typography} from '@mui/material'
import {STATUS} from 'src/constants/constants'
import {validateConsent} from 'src/pages-new/consentsAcceptance/utils'
import {useAppDispatch} from 'src/hooks'
import {updateScope} from 'src/slices/pspConfigSlice'
import apiClient from 'src/services/apiClient'
import AuthLayout from 'src/components-new/layout/AuthLayout'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import AccountStep, {AccountStepValue} from './steps/AccountStep'
import PersonalDataStep, {PersonalDataStepValue} from './steps/PersonalDataStep'
import ConsentsStep, {ConsentsStepValue} from './steps/ConsentsStep'
import ConfirmationStep from './steps/ConfirmationStep'
import {
  buildRegistrationBody,
  ConsentOption,
  isValidEmail,
  isValidPassword,
  isValidPostalCode,
} from './selfRegistrationValidation'

interface LoginConfiguration {
  scope?: string
  logoPSP?: string
  logoIA?: string
  logoSponsorPsp?: string
  sponsorText?: string
  primaryColor: string
  PSPTitleAndIcon?: {title: string, icon: string}
  firstLoginConfiguration?: {
    activationCodeRequired?: boolean
    consentCheckboxOptions?: ConsentOption[]
    [key: string]: any
  }
}

interface SelfRegistrationPageNewProps {
  config: LoginConfiguration
}

const TOTAL_STEPS = 3

interface FormState {
  email: string
  code: string
  personalData: PersonalDataStepValue
  consents: Record<string, string>
  consentPassword: string
}

const INITIAL_FORM_STATE: FormState = {
  email: '',
  code: '',
  personalData: {
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    fiscalCode: '',
    cellPhoneNumber: '',
    address: {
      country: 'Italia',
      region: '',
      province: '',
      city: '',
      address: '',
      streetNumber: '',
      postalCode: '',
      locality: '',
    },
    timeSlots: [],
  },
  consents: {},
  consentPassword: '',
}

/**
 * New-style equivalent of src/pages/selfRegistration/SelfRegistration.tsx. Reorganizes
 * the legacy's ad-hoc screens (Account → [personal data/address/timeslots merged with
 * terms/consents] → confirmation, with an inner sub-wizard buried in the middle) into 3
 * steps + a terminal confirmation screen. Data/API contract is unchanged — see
 * `buildRegistrationBody` for the exact submitted shape.
 */
export default function SelfRegistrationPageNew({config}: SelfRegistrationPageNewProps): JSX.Element {
  const dispatch = useAppDispatch()
  const [currentStep, setCurrentStep] = useState(0)
  const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [checkingAccount, setCheckingAccount] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<string>()
  const [registrationComplete, setRegistrationComplete] = useState(false)

  const firstLogin = config.firstLoginConfiguration ?? {}
  const activationCodeRequired = Boolean(firstLogin.activationCodeRequired)
  const consentOptions: ConsentOption[] = firstLogin.consentCheckboxOptions ?? []

  // Same side effects as the legacy container (SelfRegistration.tsx:23-37) and every
  // other pre-auth page (LoginPageNew.tsx:42-52): scope the PSP config so styling/labels
  // elsewhere resolve correctly, and set the tab title/favicon from the PSP branding.
  useEffect(() => {
    if (config.scope) dispatch(updateScope(config.scope))
    if (config.PSPTitleAndIcon) {
      if (config.PSPTitleAndIcon.title) document.title = config.PSPTitleAndIcon.title
      const link = document.querySelector('link[rel="shortcut icon"]')
      if (link && config.PSPTitleAndIcon.icon) {
        link.setAttribute('href', `data:image/png;base64,${config.PSPTitleAndIcon.icon}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    if (submitStatus === STATUS.FULFILLED) setRegistrationComplete(true)
  }, [submitStatus])

  const validateAccountStep = async (): Promise<boolean> => {
    const nextErrors: Record<string, string> = {}
    if (!formState.email) {
      nextErrors.email = 'Campo obbligatorio'
    } else if (!isValidEmail(formState.email)) {
      nextErrors.email = "Il formato dell'email non è corretto"
    }
    if (activationCodeRequired && !formState.code) {
      nextErrors.code = 'Campo obbligatorio'
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return false
    }

    setCheckingAccount(true)
    try {
      const isEmailValid = await apiClient.checkEmail(formState.email, () => undefined)
      if (!isEmailValid) {
        setErrors({email: "L'email non è valida"})
        return false
      }
      if (activationCodeRequired) {
        const result = await apiClient.checkRegistrationToken(formState.code, () => undefined)
        if (!result?.valid) {
          setErrors({code: 'Codice non valido'})
          return false
        }
      }
      return true
    } finally {
      setCheckingAccount(false)
    }
  }

  const validatePersonalDataStep = (): boolean => {
    const {personalData} = formState
    const {address} = personalData
    const nextErrors: Record<string, string> = {}

    if (!personalData.firstName) nextErrors.firstName = 'Campo obbligatorio'
    if (!personalData.lastName) nextErrors.lastName = 'Campo obbligatorio'
    if (!personalData.password) {
      nextErrors.password = 'Campo obbligatorio'
    } else if (!isValidPassword(personalData.password)) {
      nextErrors.password = 'Password troppo debole: almeno 8 caratteri, con una lettera maiuscola, una minuscola, un numero e un carattere speciale (tra !@#$%^&*())'
    }
    if (!personalData.confirmPassword) {
      nextErrors.confirmPassword = 'Campo obbligatorio'
    } else if (personalData.confirmPassword !== personalData.password) {
      nextErrors.confirmPassword = 'Le due password non corrispondono'
    }
    if (!personalData.fiscalCode) nextErrors.fiscalCode = 'Campo obbligatorio'
    if (!personalData.cellPhoneNumber) nextErrors.cellPhoneNumber = 'Campo obbligatorio'

    if (!address.region) nextErrors.region = 'Campo obbligatorio'
    if (!address.province) nextErrors.province = 'Campo obbligatorio'
    if (!address.city) nextErrors.city = 'Campo obbligatorio'
    if (!address.address) nextErrors.address = 'Campo obbligatorio'
    if (!address.streetNumber) nextErrors.streetNumber = 'Campo obbligatorio'
    if (!address.postalCode) {
      nextErrors.postalCode = 'Campo obbligatorio'
    } else if (!isValidPostalCode(address.postalCode)) {
      nextErrors.postalCode = 'Formato codice postale non valido'
    }

    const timeSlotsValid = personalData.timeSlots.every((slot) => slot.day && slot.from && slot.to)
    if (!timeSlotsValid) nextErrors.timeSlots = 'Compila tutti i campi delle fasce orarie'

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const validateConsentsStep = (): boolean => {
    const nextErrors: Record<string, string> = {}
    consentOptions.forEach((opt) => {
      const error = validateConsent(opt, formState.consents[opt.value])
      if (error) nextErrors[opt.value] = error
    })
    if (!formState.consentPassword) {
      nextErrors.consentPassword = 'Campo obbligatorio'
    } else if (formState.consentPassword !== formState.personalData.password) {
      nextErrors.consentPassword = 'Le password non corrispondono'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const goBack = () => {
    setErrors({})
    setCurrentStep((s) => s - 1)
  }

  const goNext = async () => {
    if (currentStep === 0) {
      if (!(await validateAccountStep())) return
    } else if (currentStep === 1) {
      if (!validatePersonalDataStep()) return
    }
    setErrors({})
    setCurrentStep((s) => s + 1)
  }

  const handleSubmit = async () => {
    if (!validateConsentsStep()) return

    const body = buildRegistrationBody({
      personalData: {
        firstName: formState.personalData.firstName,
        lastName: formState.personalData.lastName,
        password: formState.personalData.password,
        fiscalCode: formState.personalData.fiscalCode,
        cellPhoneNumber: formState.personalData.cellPhoneNumber,
        email: formState.email,
      },
      address: formState.personalData.address,
      timeSlots: formState.personalData.timeSlots,
      consentOptions,
      consents: formState.consents,
      activationCodeRequired,
      code: formState.code,
    })

    await apiClient.postSelfRegistration(body, setSubmitStatus)
  }

  const logo = config.logoPSP ? (
    <img
      alt="logoPSP"
      src={`data:image/png;base64,${config.logoPSP}`}
      style={{maxHeight: 64, maxWidth: 200, objectFit: 'contain'}}
    />
  ) : undefined

  const footer = (config.logoIA || config.logoSponsorPsp) ? (
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
      {config.logoIA && (
        <img alt="logoIA" src={`data:image/png;base64,${config.logoIA}`} style={{maxHeight: 22, objectFit: 'contain'}} />
      )}
      {config.logoSponsorPsp && (
        <Stack direction="row" alignItems="center" spacing={1}>
          {config.sponsorText && (
            <Typography variant="caption" sx={{color: NEUTRAL_SCALE[500]}}>{config.sponsorText}</Typography>
          )}
          <img
            alt="logoSponsor"
            src={`data:image/png;base64,${config.logoSponsorPsp}`}
            style={{maxHeight: 18, maxWidth: 100, objectFit: 'contain'}}
          />
        </Stack>
      )}
    </Stack>
  ) : undefined

  if (registrationComplete) {
    return (
      <AuthLayout logo={logo} footer={footer} maxWidth={720}>
        <ConfirmationStep email={formState.email} />
      </AuthLayout>
    )
  }

  const isSubmitting = submitStatus === STATUS.PENDING
  const isLastStep = currentStep === TOTAL_STEPS - 1

  return (
    <AuthLayout logo={logo} footer={footer} maxWidth={720}>
      {submitStatus === STATUS.REJECTED && (
        <InlineMessage severity="error" sx={{mb: 2}}>
          {"Si è verificato un errore durante l'invio della registrazione. Riprova."}
        </InlineMessage>
      )}

      {currentStep === 0 && (
        <AccountStep
          value={{email: formState.email, code: formState.code}}
          onChange={(next: AccountStepValue) => setFormState((prev) => ({...prev, email: next.email, code: next.code}))}
          activationCodeRequired={activationCodeRequired}
          errors={errors}
          disabled={checkingAccount}
        />
      )}

      {currentStep === 1 && (
        <PersonalDataStep
          value={formState.personalData}
          onChange={(personalData: PersonalDataStepValue) => setFormState((prev) => ({...prev, personalData}))}
          email={formState.email}
          errors={errors}
        />
      )}

      {currentStep === 2 && (
        <ConsentsStep
          firstLoginConfiguration={firstLogin}
          value={{consents: formState.consents, consentPassword: formState.consentPassword}}
          onChange={(next: ConsentsStepValue) => setFormState((prev) => ({...prev, consents: next.consents, consentPassword: next.consentPassword}))}
          errors={errors}
          disabled={isSubmitting}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', sm: 'row'},
          justifyContent: currentStep === 0 ? 'stretch' : 'space-between',
          gap: 1.5,
          mt: 3,
        }}
      >
        {currentStep > 0 && (
          <Button variant="secondary" onClick={goBack} disabled={checkingAccount || isSubmitting}>
            {'Indietro'}
          </Button>
        )}
        <Button
          variant="primary"
          fullWidth={currentStep === 0}
          onClick={isLastStep ? handleSubmit : goNext}
          loading={checkingAccount || isSubmitting}
        >
          {isLastStep ? 'Invia' : currentStep === 0 ? 'Registrami' : 'Avanti'}
        </Button>
      </Box>
    </AuthLayout>
  )
}
